import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  ArrowLeft, BookOpen, Shield, Save, Loader2, CheckCircle2, 
  AlertTriangle, FileText, Users, Heart
} from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import { useDemo } from '@/contexts/DemoContext';
import { ProtectedFeaturePage } from '@/components/ProtectedFeaturePage';

/**
 * EndOfLifePage - Will location, Power of Attorney, Funeral preferences
 * PREMIUM FEATURE - Requires Premium subscription
 */

const EndOfLifeContent = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { isDemoMode, getProfiles, getEndOfLife } = useDemo();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [endOfLife, setEndOfLife] = useState({
    will_location: '',
    power_of_attorney: '',
    healthcare_proxy: '',
    funeral_preferences: '',
    organ_donation_wishes: '',
    special_instructions: '',
    notes: ''
  });

  useEffect(() => {
    fetchProfile();
  }, [profileId, isDemoMode]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      
      if (isDemoMode) {
        const profiles = getProfiles() || [];
        const demoProfile = profiles.find(p => p.id === profileId) || profiles[0];
        const demoEndOfLife = getEndOfLife() || {};
        setProfile(demoProfile);
        setEndOfLife(prev => ({ ...prev, ...demoEndOfLife }));
        setLoading(false);
        return;
      }
      
      const response = await API.get(`/emergency-binder/profiles/${profileId}`);
      setProfile(response.data);
      if (response.data.end_of_life) {
        setEndOfLife(prev => ({ ...prev, ...response.data.end_of_life }));
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      if (!isDemoMode) {
        toast.error('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (isDemoMode) {
        toast.success('End-of-life information saved (demo)');
        setSaving(false);
        return;
      }
      
      await API.put(`/emergency-binder/profiles/${profileId}`, {
        end_of_life: endOfLife
      });
      toast.success('End-of-life information saved');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setEndOfLife(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="end-of-life-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/emergency-binder')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-slate-800">End-of-Life Planning</h1>
              <p className="text-xs text-slate-500">{profile?.name}</p>
            </div>
            <Button onClick={handleSave} disabled={saving} className="bg-slate-700 hover:bg-slate-800">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Privacy Warning */}
        <Card className="border-slate-300 bg-slate-100">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-slate-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-slate-800 mb-1">Sensitive Information</h3>
                <p className="text-sm text-slate-600">
                  This section contains sensitive personal information. Only share access 
                  with trusted family members or legal representatives.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Will & Legal Documents */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-5 w-5 text-slate-500" />
              Legal Documents
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Will Location</Label>
              <Input
                value={endOfLife.will_location || ''}
                onChange={(e) => updateField('will_location', e.target.value)}
                placeholder="Where is the will stored? (e.g., safe deposit box, attorney's office)"
              />
            </div>
            <div className="space-y-2">
              <Label>Power of Attorney</Label>
              <Input
                value={endOfLife.power_of_attorney || ''}
                onChange={(e) => updateField('power_of_attorney', e.target.value)}
                placeholder="Who has power of attorney? (name and contact)"
              />
            </div>
            <div className="space-y-2">
              <Label>Healthcare Proxy / Medical Power of Attorney</Label>
              <Input
                value={endOfLife.healthcare_proxy || ''}
                onChange={(e) => updateField('healthcare_proxy', e.target.value)}
                placeholder="Who can make medical decisions? (name and contact)"
              />
            </div>
          </CardContent>
        </Card>

        {/* Wishes & Preferences */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Heart className="h-5 w-5 text-rose-500" />
              Wishes & Preferences
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Organ Donation Wishes</Label>
              <Textarea
                value={endOfLife.organ_donation_wishes || ''}
                onChange={(e) => updateField('organ_donation_wishes', e.target.value)}
                placeholder="Your wishes regarding organ donation"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Funeral Preferences</Label>
              <Textarea
                value={endOfLife.funeral_preferences || ''}
                onChange={(e) => updateField('funeral_preferences', e.target.value)}
                placeholder="Burial, cremation, memorial service preferences, etc."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Special Instructions</Label>
              <Textarea
                value={endOfLife.special_instructions || ''}
                onChange={(e) => updateField('special_instructions', e.target.value)}
                placeholder="Any special requests or instructions for family"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Additional Notes */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-slate-500" />
              Additional Notes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={endOfLife.notes || ''}
              onChange={(e) => updateField('notes', e.target.value)}
              placeholder="Any other important information family should know..."
              rows={4}
            />
          </CardContent>
        </Card>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full h-12 bg-slate-700 hover:bg-slate-800"
        >
          {saving ? (
            <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</>
          ) : (
            <><CheckCircle2 className="h-4 w-4 mr-2" />Save End-of-Life Information</>
          )}
        </Button>
      </main>
    </div>
  );
};

// Wrap content with feature protection - Premium tier required
const EndOfLifePage = () => (
  <ProtectedFeaturePage
    feature="end_of_life_planning"
    title="End-of-Life Planning"
    description="Store important information about wills, power of attorney, healthcare directives, and final wishes."
    backPath="/emergency-binder"
  >
    <EndOfLifeContent />
  </ProtectedFeaturePage>
);

export default EndOfLifePage;
